import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { AssistantState } from '../hooks/useAssistantState';

interface CoordonneesStepProps {
  state: AssistantState;
  onComplete: () => void;
}

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const CoordonneesStep = ({ state, onComplete }: CoordonneesStepProps) => {
  const [coordonnees, setCoordonnees] = useState({
    nom: '',
    prenom: '',
    telephone: '',
    email: '',
    preferenceContact: 'email' as 'email' | 'telephone' | 'whatsapp',
    rgpdAccepted: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!emailRegex.test(coordonnees.email.trim())) {
      toast.error('Merci d’indiquer une adresse email valide.');
      return;
    }
    if (coordonnees.telephone.trim().length < 8) {
      toast.error('Merci d’indiquer un numéro de téléphone valide.');
      return;
    }

    setIsSubmitting(true);
    try {
      // L'assistant ne traite que particuliers et entreprises ; les collectivités
      // sont orientées vers leur formulaire dédié en amont.
      const isPro = state.userType === 'professionnel';
      const guests = state.nombreInvites ?? state.nombreParticipants ?? null;
      const lieu = state.adresseLieu
        ? [
            state.adresseLieu.rue,
            `${state.adresseLieu.codePostal ?? ''} ${state.adresseLieu.ville ?? ''}`.trim(),
            state.adresseLieu.typeLieu,
          ].filter(Boolean).join(', ')
        : '';

      const notes = [
        'Demande via l’assistant en ligne',
        state.budgetFourchette ? `Budget envisagé : ${state.budgetFourchette}` : null,
        state.objectif ? `Objectif : ${state.objectif}` : null,
        state.dateAlternative ? `Date alternative : ${state.dateAlternative}` : null,
        lieu ? `Lieu : ${lieu}` : null,
        state.selectedOffers && state.selectedOffers.length
          ? `Prestations envisagées : ${state.selectedOffers.map((o: any) => o.title).filter(Boolean).join(', ')}`
          : null,
        `Préférence de contact : ${coordonnees.preferenceContact}`,
      ].filter(Boolean).join('\n');

      // Tableau des prestations (réutilisé pour l'insert ET l'email de confirmation).
      const servicesArray = (state.selectedOffers && state.selectedOffers.length)
        ? state.selectedOffers.map((o: any) => {
            const ut = String(o.unitType || '').toLowerCase();
            const price_unit = ut.includes('personne')
              ? 'par_personne'
              : ut.includes('heure')
              ? 'par_heure'
              : ut.includes('jour')
              ? 'par_jour'
              : 'par_prestation';
            return {
              id: o.id,
              title: o.title,
              price: o.price ?? 0,
              price_unit,
              quantity: o.quantity || 1,
              category: o.category ?? null,
            };
          })
        : [];

      const { error } = await supabase.from('event_requests').insert({
        first_name: coordonnees.prenom.trim(),
        last_name: coordonnees.nom.trim(),
        email: coordonnees.email.trim(),
        phone: coordonnees.telephone.trim(),
        event_type: state.typeEvenement || null,
        date: state.dateEvenement || null,
        nombre_invites: guests,
        budget_estime: state.budgetIndicatif ?? null,
        // Transfert COMPLET vers l'admin : l'éditeur de devis (QuoteGenerator →
        // prepareQuoteData) lit id / title / price / price_unit / quantity /
        // category pour préremplir le devis. On conserve donc le prix indicatif
        // et l'unité (le client ne les voit plus, mais l'équipe en a besoin).
        services_selected: servicesArray.length ? JSON.stringify(servicesArray) : null,
        notes,
        is_professional: isPro,
        status: 'en_attente',
      });

      if (error) throw error;

      // Notification équipe pour les demandes entreprises (edge function dédiée)
      if (isPro) {
        try {
          await supabase.functions.invoke('send-professional-request-notification', {
            body: {
              company: '',
              firstName: coordonnees.prenom.trim(),
              lastName: coordonnees.nom.trim(),
              email: coordonnees.email.trim(),
              phone: coordonnees.telephone.trim(),
              fonction: '',
              eventType: state.typeEvenement || '',
              estimatedDate: state.dateEvenement || '',
              nombreInvites: guests ? String(guests) : '',
              budgetEstime: state.budgetIndicatif ? String(state.budgetIndicatif) : '',
              message: notes,
            },
          });
        } catch (notifErr) {
          console.error('Notification assistant échouée:', notifErr);
        }
      }

      // Synchronisation contact Brevo (best-effort)
      try {
        await supabase.functions.invoke('send-to-brevo', {
          body: {
            firstName: coordonnees.prenom.trim(),
            lastName: coordonnees.nom.trim(),
            email: coordonnees.email.trim(),
            phone: coordonnees.telephone.trim(),
          },
        });
      } catch (brevoErr) {
        console.error('Brevo assistant échoué:', brevoErr);
      }

      // Parité avec le parcours /devis : email de confirmation au client + récap à l'équipe
      // (réutilise la fonction déployée send-devis-notification, best-effort).
      try {
        await supabase.functions.invoke('send-devis-notification', {
          body: {
            first_name: coordonnees.prenom.trim(),
            last_name: coordonnees.nom.trim(),
            email: coordonnees.email.trim(),
            phone: coordonnees.telephone.trim(),
            event_type: state.typeEvenement || null,
            date: state.dateEvenement || null,
            nb_guests: guests,
            prestation_duration: null,
            services_selected: servicesArray,
            notes,
            // Pas de PDF chiffré au client : aucun tarif n'a été montré dans l'assistant.
            source: 'assistant',
          },
        });
      } catch (notifErr) {
        console.error('Email de confirmation assistant échoué:', notifErr);
      }

      // Parité liste Brevo « Demande devis » (comme /devis)
      try {
        await supabase.functions.invoke('brevo-devis-particuliers', {
          body: {
            firstName: coordonnees.prenom.trim(),
            lastName: coordonnees.nom.trim(),
            email: coordonnees.email.trim(),
            phone: coordonnees.telephone.trim(),
          },
        });
      } catch (brevoErr2) {
        console.error('Brevo devis (assistant) échoué:', brevoErr2);
      }

      onComplete();
    } catch (err: any) {
      console.error('Erreur soumission assistant:', err);
      toast.error(err?.message || 'Une erreur est survenue. Réessayez dans un instant.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const canSubmit =
    coordonnees.nom &&
    coordonnees.prenom &&
    coordonnees.email &&
    coordonnees.telephone &&
    coordonnees.rgpdAccepted &&
    !isSubmitting;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="prenom">Prénom *</Label>
          <Input
            id="prenom"
            value={coordonnees.prenom}
            onChange={(e) => setCoordonnees(prev => ({ ...prev, prenom: e.target.value }))}
            placeholder="Votre prénom"
            autoComplete="given-name"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="nom">Nom *</Label>
          <Input
            id="nom"
            value={coordonnees.nom}
            onChange={(e) => setCoordonnees(prev => ({ ...prev, nom: e.target.value }))}
            placeholder="Votre nom"
            autoComplete="family-name"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="email">Email *</Label>
        <Input
          id="email"
          type="email"
          value={coordonnees.email}
          onChange={(e) => setCoordonnees(prev => ({ ...prev, email: e.target.value }))}
          placeholder="votre@email.com"
          autoComplete="email"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="telephone">Téléphone *</Label>
        <Input
          id="telephone"
          type="tel"
          value={coordonnees.telephone}
          onChange={(e) => setCoordonnees(prev => ({ ...prev, telephone: e.target.value }))}
          placeholder="06 12 34 56 78"
          autoComplete="tel"
        />
      </div>

      <div className="space-y-2">
        <Label>Préférence de contact</Label>
        <Select
          value={coordonnees.preferenceContact}
          onValueChange={(value: any) => setCoordonnees(prev => ({ ...prev, preferenceContact: value }))}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Comment souhaitez-vous être contacté ?" />
          </SelectTrigger>
          <SelectContent position="popper" side="bottom" align="start" className="z-[9999] bg-background border border-border shadow-lg min-w-full">
            <SelectItem value="email">Par email</SelectItem>
            <SelectItem value="telephone">Par téléphone</SelectItem>
            <SelectItem value="whatsapp">Par WhatsApp</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center space-x-2">
        <Checkbox
          id="rgpd"
          checked={coordonnees.rgpdAccepted}
          onCheckedChange={(checked) => setCoordonnees(prev => ({ ...prev, rgpdAccepted: !!checked }))}
        />
        <Label htmlFor="rgpd" className="text-sm">
          J'accepte d'être recontacté·e par Maison Eridia au sujet de mon projet *
        </Label>
      </div>

      <Button
        onClick={handleSubmit}
        disabled={!canSubmit}
        className="w-full"
      >
        {isSubmitting ? (
          <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Envoi en cours…</>
        ) : (
          'Envoyer ma demande'
        )}
      </Button>

      <p className="text-center text-xs text-muted-foreground">
        Vos coordonnées restent confidentielles et ne sont jamais cédées à des tiers.
      </p>
    </div>
  );
};
